// Mobilní ovládání výpisu (issue #14, screens/m04-category.md §6 a dál).
//
// Mobilní UA render Shoptetu nemá sidebar ANI nativní tlačítko filtru (`#filters`
// v DOM je, jen bez ovládání) — dodáváme tedy:
//   • sticky lištu s pilulkami „Filtrovat" (s počítadlem) a řazení + řádek chipů
//   • off-canvas filtr 330 px zprava se sticky patkou „Zobrazit N produktů"
//   • bottom-sheet řazení nad nativním `.listSorting`
//
// Filtrování i řazení zůstává 100% nativní. `#filters` se NIKAM nestěhuje —
// obalíme ho v místě (`.bs-fsheet`), takže ho default JS Shoptetu pořád najde
// ve svém kontejneru; na desktopu je celý obal `display: contents`, takže
// rozvržení z issue #13 zůstává beze změny.
//
// Počet v CTA čte nativní „Položek k zobrazení" (`.filter-total-count`), které
// Shoptet přepočítává živě; fallback je počet z hlavičky výpisu.
//
// Výpis z vlastního indexu (saleBrowse.js, registr listingSource.js) dodá chipy,
// řazení i počet do patky sám; po změně výběru volá `refreshMobileFilter()`.
//
// Idempotentní, re-init po AJAX překreslení (ShoptetDOMPageContentLoaded).

import { icon } from './icon.js';
import { activeFilters, clearFilters, flushListingScroll, listingStats, productsWord } from './categoryPage.js';
import { sortOptions } from './listingTools.js';
import { listingSource } from './listingSource.js';
import L from '../locale/cs/mobileFilter.js';

const LABEL_FILTER = L.filter;
const LABEL_FILTER_TITLE = L.filterTitle;
const LABEL_FILTER_CLOSE = L.filterClose;
const LABEL_SORT_OPEN = L.sortOpen;
// tlačítko nese zvolené řazení („Řadit: Doporučujeme") — redesign výpisu
const LABEL_SORT_PREFIX = L.sortPrefix;
const LABEL_SORT_TITLE = L.sortTitle;
const LABEL_CLEAR = L.clear;
const LABEL_CLEAR_ALL = L.clearAll;
const LABEL_SHOW_ALL = L.showAll;

const OPEN_FILTER = 'bs-fsheet-open';
const OPEN_SORT = 'bs-ssheet-open';

// Dokud třída nesedí, zůstává na mobilu viditelný desktopový toolbar se sort taby —
// když boot spadne, uživatel neztratí řazení (stejná pojistka jako `bs-header-ready`).
const READY = 'bs-mfilter-ready';

let lastTrigger = null;
let globalWired = false;

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function isListing() {
  const b = document.body.classList;
  return b.contains('type-category') || b.contains('type-manufacturer-detail');
}

// --------------------------------------------------------------- otevírání

function syncExpanded() {
  const open = document.body.classList;
  const pairs = [
    ['.bs-mbar__btn--filter', OPEN_FILTER],
    ['.bs-mbar__btn--sort', OPEN_SORT],
  ];
  pairs.forEach(function (pair) {
    const btn = document.querySelector(pair[0]);
    if (btn) btn.setAttribute('aria-expanded', String(open.contains(pair[1])));
  });
}

function closeSheets() {
  const body = document.body.classList;
  if (!body.contains(OPEN_FILTER) && !body.contains(OPEN_SORT)) return;
  body.remove(OPEN_FILTER);
  body.remove(OPEN_SORT);
  syncExpanded();
  if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  lastTrigger = null;
  // změna filtru proběhla s otevřeným sheetem (body overflow: hidden) → odložený
  // scroll na začátek produktů se pouští až teď; bez změny filtru je to no-op
  flushListingScroll();
}

function openSheet(cls, trigger, focusTarget) {
  const body = document.body.classList;
  body.remove(OPEN_FILTER);
  body.remove(OPEN_SORT);
  body.add(cls);
  syncExpanded();
  lastTrigger = trigger || null;
  if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
}

// --------------------------------------------------------------- počty

// Nativní „Položek k zobrazení: N" (Shoptet ho přepočítává i bez překreslení)
function totalCount() {
  const source = listingSource();
  if (source) {
    const total = source.stats().total;
    return isFinite(total) ? total : null;
  }
  const native = document.querySelector('#filters .filter-total-count');
  if (native) {
    const n = parseInt((native.textContent || '').replace(/\D/g, ''), 10);
    if (isFinite(n)) return n;
  }
  const stats = listingStats();
  return isFinite(stats.total) ? stats.total : null;
}

function refreshApply() {
  const apply = document.querySelector('.bs-fsheet__apply');
  if (apply) {
    const n = totalCount();
    apply.textContent = n == null ? LABEL_SHOW_ALL : L.show(n, productsWord(n));
  }
  const clear = document.querySelector('.bs-fsheet__clear');
  if (clear) {
    const active = activeFilters().length > 0;
    clear.classList.toggle('is-active', active);
    clear.disabled = !active;
  }
}

function observeCount() {
  const box = document.querySelector('#filters .filter-section-count');
  if (!box || box.dataset.bsCount) return;
  box.dataset.bsCount = '1';
  new MutationObserver(refreshApply).observe(box, { childList: true, characterData: true, subtree: true });
}

// --------------------------------------------------------------- chipy

function renderChips(box, filters) {
  if (!box) return;
  box.textContent = '';

  filters.forEach(function (item) {
    const chip = el('button', 'bs-chip bs-chip--filter', item.label);
    chip.type = 'button';
    chip.appendChild(icon('close'));
    chip.addEventListener('click', item.remove);
    box.appendChild(chip);
  });

  if (filters.length) {
    const all = el('button', 'bs-mbar__clear', LABEL_CLEAR_ALL);
    all.type = 'button';
    all.addEventListener('click', clearFilters);
    box.appendChild(all);
  }

  box.classList.toggle('is-empty', filters.length === 0);
}

// --------------------------------------------------------------- off-canvas filtr

function mountFilterSheet() {
  const filters = document.querySelector('#filters');
  if (!filters) return null;
  const mounted = filters.closest('.bs-fsheet');
  if (mounted) return mounted;

  const sheet = el('div', 'bs-fsheet');

  const backdrop = el('div', 'bs-fsheet__backdrop');
  backdrop.addEventListener('click', closeSheets);

  const panel = el('div', 'bs-fsheet__panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', LABEL_FILTER_TITLE);

  const head = el('div', 'bs-fsheet__head');
  const title = el('span', 'bs-fsheet__title');
  title.appendChild(icon('filter'));
  title.appendChild(el('span', null, LABEL_FILTER_TITLE));
  const close = el('button', 'bs-fsheet__close');
  close.type = 'button';
  close.setAttribute('aria-label', LABEL_FILTER_CLOSE);
  close.appendChild(icon('close'));
  close.addEventListener('click', closeSheets);
  head.appendChild(title);
  head.appendChild(close);

  const body = el('div', 'bs-fsheet__body');

  // patka je sticky: reset vlevo, návrat do výpisu s živým počtem vpravo
  const foot = el('div', 'bs-fsheet__foot');
  const clear = el('button', 'bs-fsheet__clear', LABEL_CLEAR);
  clear.type = 'button';
  clear.addEventListener('click', clearFilters);
  const apply = el('button', 'bs-fsheet__apply', LABEL_SHOW_ALL);
  apply.type = 'button';
  apply.addEventListener('click', closeSheets);
  foot.appendChild(clear);
  foot.appendChild(apply);

  panel.appendChild(head);
  panel.appendChild(body);
  panel.appendChild(foot);
  sheet.appendChild(backdrop);
  sheet.appendChild(panel);

  // obal vzniká v místě nativního `#filters` (viz hlavička souboru)
  filters.insertAdjacentElement('beforebegin', sheet);
  body.appendChild(filters);

  filters.addEventListener('change', function () {
    refresh();
  });
  observeCount();

  return sheet;
}

// --------------------------------------------------------------- bottom-sheet řazení

function mountSortSheet() {
  const options = sortOptions();
  if (!options.length) return null;

  let sheet = document.querySelector('.bs-ssheet');
  if (!sheet) {
    sheet = el('div', 'bs-ssheet');

    const backdrop = el('div', 'bs-ssheet__backdrop');
    backdrop.addEventListener('click', closeSheets);

    const panel = el('div', 'bs-ssheet__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', LABEL_SORT_TITLE);
    panel.appendChild(el('span', 'bs-ssheet__grip'));
    panel.appendChild(el('div', 'bs-ssheet__title', LABEL_SORT_TITLE));
    panel.appendChild(el('div', 'bs-ssheet__rows'));

    sheet.appendChild(backdrop);
    sheet.appendChild(panel);
    document.body.appendChild(sheet);
  }

  // řádky se staví znovu — po AJAX překreslení míří staré na zahozené uzly
  const rows = sheet.querySelector('.bs-ssheet__rows');
  rows.textContent = '';
  options.forEach(function (opt) {
    const row = el('button', 'bs-ssheet__row');
    row.type = 'button';
    if (opt.active) {
      row.classList.add('is-active');
      row.setAttribute('aria-current', 'true');
    }
    row.appendChild(el('span', null, opt.label));
    row.appendChild(icon('check'));
    row.addEventListener('click', function () {
      closeSheets();
      opt.activate();
    });
    rows.appendChild(row);
  });

  return sheet;
}

// --------------------------------------------------------------- sticky lišta

function buildFilterButton() {
  const btn = el('button', 'bs-mbar__btn bs-mbar__btn--filter');
  btn.type = 'button';
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-expanded', 'false');
  btn.appendChild(icon('filter'));
  btn.appendChild(el('span', null, LABEL_FILTER));
  btn.appendChild(el('span', 'bs-mbar__badge is-empty', '0'));
  btn.addEventListener('click', function () {
    openSheet(OPEN_FILTER, btn, document.querySelector('.bs-fsheet__close'));
  });
  return btn;
}

function buildSortButton() {
  const btn = el('button', 'bs-mbar__btn bs-mbar__btn--sort');
  btn.type = 'button';
  btn.setAttribute('aria-label', LABEL_SORT_TITLE);
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-expanded', 'false');
  btn.appendChild(el('span', 'bs-mbar__sort-label', LABEL_SORT_OPEN));
  btn.appendChild(icon('chevron-down'));
  btn.addEventListener('click', function () {
    openSheet(OPEN_SORT, btn, document.querySelector('.bs-ssheet__row'));
  });
  return btn;
}

// Lišta je sourozencem `#category-header` (ne jeho dítětem) — sticky prvek se
// drží jen v rámci rodiče, a ten musí sahat přes celý výpis.
function mountBar() {
  const header = document.querySelector('#category-header');
  const host = header && header.parentElement;
  if (!host) return null;

  const mounted = host.querySelector(':scope > .bs-mbar');
  if (mounted) return mounted;

  const row = el('div', 'bs-mbar__row');
  if (document.querySelector('#filters')) row.appendChild(buildFilterButton());
  if (sortOptions().length) row.appendChild(buildSortButton());
  if (!row.children.length) return null;

  const bar = el('div', 'bs-mbar');
  bar.appendChild(row);
  bar.appendChild(el('div', 'bs-mbar__chips is-empty'));
  header.insertAdjacentElement('afterend', bar);
  return bar;
}

// --------------------------------------------------------------- stav

function refresh() {
  const filters = activeFilters();
  const bar = document.querySelector('.bs-mbar');

  if (bar) {
    const filterBtn = bar.querySelector('.bs-mbar__btn--filter');
    if (filterBtn) {
      filterBtn.classList.toggle('is-active', filters.length > 0);
      const badge = filterBtn.querySelector('.bs-mbar__badge');
      if (badge) {
        badge.textContent = String(filters.length);
        badge.classList.toggle('is-empty', filters.length === 0);
      }
    }

    const sortLabel = bar.querySelector('.bs-mbar__sort-label');
    if (sortLabel) {
      const active = sortOptions().filter(function (o) {
        return o.active;
      })[0];
      sortLabel.textContent = active ? LABEL_SORT_PREFIX + active.label : LABEL_SORT_OPEN;
    }

    renderChips(bar.querySelector('.bs-mbar__chips'), filters);
  }

  refreshApply();
}

function wireGlobal() {
  if (globalWired) return;
  globalWired = true;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeSheets();
  });
}

/** Dorovná lištu, chipy, řazení a patku po změně výběru mimo nativní filtr. */
export function refreshMobileFilter() {
  if (!document.querySelector('.bs-mbar, .bs-fsheet')) return;
  mountSortSheet();
  refresh();
}

export function initMobileFilter() {
  if (!isListing()) return;
  if (!document.querySelector('#products')) return;

  mountFilterSheet();
  const bar = mountBar();
  mountSortSheet();
  refresh();
  wireGlobal();

  // Lišta stojí → teprve teď smí CSS na mobilu schovat desktopový toolbar
  if (bar) document.documentElement.classList.add(READY);
}
